import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';

const CartCtx = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState([]); // {sku,name,price,qty}
  const [discountAmount, setDiscountAmount] = useState(0);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [taxRate, setTaxRate] = useState(() => {
    const s = localStorage.getItem('pos_settings');
    return s ? (JSON.parse(s).taxRate ?? 0.075) : 0.075; // Default to 7.5% tax
  });

  useEffect(() => {
    // Listen for storage changes from other tabs
    const sub = (e) => {
      if (e.key === 'pos_settings') {
        const s = JSON.parse(e.newValue || '{}');
        setTaxRate(s.taxRate ?? 0.075); // Default to 7.5%
      }
    };
    window.addEventListener('storage', sub);
    
    // Also listen for a custom event for same-window updates
    const customSub = () => {
      const s = localStorage.getItem('pos_settings');
      if (s) {
        const parsed = JSON.parse(s);
        setTaxRate(parsed.taxRate ?? 0.075); // Default to 7.5%
      }
    };
    window.addEventListener('taxRateUpdated', customSub);
    
    // Re-read on window focus (when coming back from Settings)
    const focusSub = () => {
      const s = localStorage.getItem('pos_settings');
      if (s) {
        const parsed = JSON.parse(s);
        setTaxRate(parsed.taxRate ?? 0.075); // Default to 7.5%
      }
    };
    window.addEventListener('focus', focusSub);
    
    return () => {
      window.removeEventListener('storage', sub);
      window.removeEventListener('taxRateUpdated', customSub);
      window.removeEventListener('focus', focusSub);
    };
  }, []);

  const addItem = (prod) => {
    console.log('[CART] addItem called with:', {
      name: prod.name,
      sku: prod.sku,
      taxable: prod.taxable,
      taxableType: typeof prod.taxable
    });
    
    setItems((prev) => {
      const idx = prev.findIndex((p) => p.sku === prod.sku);
      if (idx >= 0) {
        const cp = [...prev];
        cp[idx] = { ...cp[idx], qty: cp[idx].qty + 1, price: prod.price };
        console.log('[CART] Updated existing item quantity');
        return cp;
      }
      
      const finalTaxable = prod.taxable !== undefined ? prod.taxable : true;
      console.log('[CART] Adding new item with taxable =', finalTaxable);
      
      return [...prev, { 
        sku: prod.sku, 
        name: prod.name, 
        price: prod.price, 
        qty: 1,
        variantName: prod.variantName,
        image: prod.image,
        price_id: prod.price_id,
        taxable: finalTaxable,
        description: prod.description || ''
      }];
    });
  };

  const updateQty = (sku, qty) => setItems((prev) => prev.map((p) => p.sku === sku ? { ...p, qty: Math.max(1, qty) } : p));
  const removeItem = (sku) => setItems((prev) => prev.filter((p) => p.sku !== sku));
  const clear = () => { setItems([]); setDiscountAmount(0); setDiscountPercent(0); };

  const subtotal = useMemo(() => items.reduce((s, i) => s + i.qty * i.price, 0), [items]);
  const discountTotal = useMemo(() => {
    let d = discountAmount || 0;
    if (discountPercent) d += (discountPercent / 100) * subtotal;
    return d;
  }, [discountAmount, discountPercent, subtotal]);
  
  // Calculate tax only on taxable items
  const tax = useMemo(() => {
    if (items.length === 0) return 0;
    
    // Calculate subtotal of taxable items only
    const taxableSubtotal = items
      .filter(item => item.taxable !== false) // Default to taxable if not specified
      .reduce((s, i) => s + i.qty * i.price, 0);
    
    console.log('[TAX CALC] Items:', items);
    console.log('[TAX CALC] Taxable subtotal:', taxableSubtotal);
    console.log('[TAX CALC] Total subtotal:', subtotal);
    console.log('[TAX CALC] Tax rate:', taxRate);
    
    // If no taxable items, return 0
    if (taxableSubtotal === 0) return 0;
    
    // Apply discount proportionally to taxable items
    let taxableDiscountPortion = 0;
    if (discountTotal > 0 && subtotal > 0) {
      taxableDiscountPortion = (taxableSubtotal / subtotal) * discountTotal;
    }
    
    console.log('[TAX CALC] Discount on taxable items:', taxableDiscountPortion);
    
    // Calculate tax on taxable items after discount
    const calculatedTax = (taxableSubtotal - taxableDiscountPortion) * taxRate;
    console.log('[TAX CALC] Final tax:', calculatedTax);
    
    return calculatedTax;
  }, [items, subtotal, discountTotal, taxRate]);
  
  const total = useMemo(() => Math.max(0, +(subtotal - discountTotal + tax).toFixed(2)), [subtotal, discountTotal, tax]);

  return (
    <CartCtx.Provider value={{ items, addItem, updateQty, removeItem, clear, subtotal, discountTotal, tax, total, discountAmount, setDiscountAmount, discountPercent, setDiscountPercent, taxRate }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
